import React, { Suspense, lazy } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { Card } from '@/components/ui/card'

interface LazyWrapperProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  className?: string
}

// Default loading fallback
const DefaultFallback = ({ className }: { className?: string }) => (
  <Card className={`p-6 space-y-4 ${className || ''}`}>
    <Skeleton className="h-6 w-1/3" />
    <div className="space-y-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
    </div>
    <div className="flex gap-2">
      <Skeleton className="h-8 w-20" />
      <Skeleton className="h-8 w-20" />
    </div>
  </Card>
)

export function LazyWrapper({ children, fallback, className }: LazyWrapperProps) {
  const defaultFallback = <DefaultFallback className={className} />
  
  return (
    <Suspense fallback={fallback || defaultFallback}>
      {children}
    </Suspense>
  )
}

// Lazy load heavy components
export const LazyAnalyticsDashboard = lazy(() => 
  import('@/components/AnalyticsDashboard').then(module => ({ default: module.AnalyticsDashboard }))
)

export const LazyResponsiveInvoiceList = lazy(() => 
  import('@/components/ResponsiveInvoiceList').then(module => ({ default: module.ResponsiveInvoiceList }))
)

export const LazyEmailPreview = lazy(() => 
  import('@/components/EmailPreview').then(module => ({ default: module.EmailPreview }))
)

export const LazyUserManagement = lazy(() => 
  import('@/components/UserManagement').then(module => ({ default: module.UserManagement }))
)

// Analytics Dashboard fallback
export const AnalyticsDashboardSkeleton = () => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="p-6">
          <div className="flex flex-row items-center justify-between space-y-0 pb-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-4" />
          </div>
          <Skeleton className="h-8 w-16" />
          <Skeleton className="h-3 w-12 mt-2" />
        </Card>
      ))}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <Card key={i} className="p-6">
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <div className="space-y-2">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-8" />
              </div>
              <div className="flex justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  </div>
)

// Invoice List fallback
export const InvoiceListSkeleton = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <Skeleton className="h-6 w-40" />
      <Skeleton className="h-6 w-24" />
    </div>
    
    {/* Desktop skeleton */}
    <div className="hidden lg:block border rounded-lg">
      <div className="p-4 border-b">
        <div className="grid grid-cols-7 gap-4">
          {[1, 2, 3, 4, 5, 6, 7].map((i) => (
            <Skeleton key={i} className="h-4" />
          ))}
        </div>
      </div>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="p-4 border-b last:border-b-0">
          <div className="grid grid-cols-7 gap-4 items-center">
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
            <Skeleton className="h-4" />
            <div className="flex gap-2 justify-end">
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-8" />
              <Skeleton className="h-8 w-16" />
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Mobile skeleton */}
    <div className="lg:hidden space-y-4">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="p-4 space-y-3">
          <div className="flex justify-between items-start">
            <div className="space-y-2 flex-1">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="text-right space-y-2">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-12" />
          </div>
        </Card>
      ))}
    </div>
  </div>
) 